import type { z } from 'zod';

import {
  BusNextServicesBlockSchema,
  FlightNextServicesBlockSchema,
  HotelNextServicesBlockSchema,
  MobilityNextServicesBlockSchema,
  RentalNextServicesBlockSchema,
  type NextServiceSuggestion,
  type NextServicesBlockMap,
  type NextServicesProduct,
} from './next_services_schema.js';

/**
 * O catálogo default de cada produto — a ordem do card e o selo «Recomendado».
 *
 * - Voo (design #2116): sem assento (#4053) — a escolha de assento só existe
 *   com o voucher EMITIDO, e o card nasce no turno da reserva, sempre antes da
 *   emissão. O assento é oferecido pelo chip do itinerário e pela saia do
 *   voucher. `seat` segue no schema para os cards do histórico.
 * - Hotel (#2349): o aéreo fica de fora até o produto decidir.
 * - Ônibus (frame 09): hotel, depois voos, carro e seguro.
 * - Carro (#5514): só o hotel. Sem seguro: não há domínio de seguro para atender
 *   a linha.
 * - Corrida (#5514): hotel, depois o carro.
 */
export const NEXT_SERVICES_DEFAULTS: {
  readonly [P in NextServicesProduct]: readonly NextServiceSuggestion[];
} = {
  flight: [
    { service: 'hotel', recommended: true },
    { service: 'car' },
    { service: 'train' },
    { service: 'bus' },
    { service: 'insurance' },
  ],
  hotel: [
    { service: 'car', recommended: true },
    { service: 'train' },
    { service: 'bus' },
    { service: 'insurance' },
  ],
  bus: [
    { service: 'hotel', recommended: true },
    { service: 'flight' },
    { service: 'car' },
    { service: 'insurance' },
  ],
  rental: [{ service: 'hotel', recommended: true }],
  mobility: [{ service: 'hotel', recommended: true }, { service: 'car' }],
};

interface SchemaMap {
  flight: typeof FlightNextServicesBlockSchema;
  hotel: typeof HotelNextServicesBlockSchema;
  bus: typeof BusNextServicesBlockSchema;
  rental: typeof RentalNextServicesBlockSchema;
  mobility: typeof MobilityNextServicesBlockSchema;
}

/** O que o autor do bloco passa: `kind` sai do produto, `id` cai no kind e
 *  `services`, no catálogo default. */
type InputOf<S extends z.ZodType> = Omit<z.input<S>, 'kind' | 'id' | 'services'> & {
  id?: string;
  services?: NextServiceSuggestion[];
};
type NextServicesInput<P extends NextServicesProduct> = InputOf<SchemaMap[P]>;

function builderOf<S extends z.ZodType>(
  schema: S,
  kind: string,
  defaults: readonly NextServiceSuggestion[],
): (input: InputOf<S>) => z.output<S> {
  return (input) => {
    const { id = kind, services, ...rest } = input;
    return schema.parse({ id, kind, ...rest, services: services ?? [...defaults] });
  };
}

const BUILDERS: {
  readonly [P in NextServicesProduct]: (input: NextServicesInput<P>) => NextServicesBlockMap[P];
} = {
  flight: builderOf(
    FlightNextServicesBlockSchema,
    'flight_next_services',
    NEXT_SERVICES_DEFAULTS.flight,
  ),
  hotel: builderOf(
    HotelNextServicesBlockSchema,
    'hotel_next_services',
    NEXT_SERVICES_DEFAULTS.hotel,
  ),
  bus: builderOf(BusNextServicesBlockSchema, 'bus_next_services', NEXT_SERVICES_DEFAULTS.bus),
  rental: builderOf(
    RentalNextServicesBlockSchema,
    'rental_next_services',
    NEXT_SERVICES_DEFAULTS.rental,
  ),
  mobility: builderOf(
    MobilityNextServicesBlockSchema,
    'mobility_next_services',
    NEXT_SERVICES_DEFAULTS.mobility,
  ),
};

/** Autoria determinística do bloco (agents); valida e lança em shape inválido. */
export function buildNextServices<P extends NextServicesProduct>(
  product: P,
  input: NextServicesInput<P>,
): NextServicesBlockMap[P] {
  return BUILDERS[product](input);
}
